import { TOOLBAR_SELECTORS } from '@/modules/base/components/inputs/html-input/_helpers/constants'

/**
 * Checks if an element is part of the toolbar or a related UI component
 */
export const isToolbarElement = (element: HTMLElement): boolean => {
  return TOOLBAR_SELECTORS.some(selector => element.closest(selector))
}

/**
 * Creates a toolbar interaction handler with automatic cleanup
 * Supports both mouse and touch events for mobile compatibility
 */
export const createToolbarInteractionHandler = (
  interactionRef: React.MutableRefObject<boolean>,
  timeout: number = 100,
) => {
  return (e: React.MouseEvent | React.TouchEvent) => {
    // Mark that we're interacting with toolbar
    interactionRef.current = true

    // Only prevent default if we're not clicking on an input element
    // This allows HEX input fields in color pickers to be clickable
    const target = e.target as HTMLElement
    const isInputElement = target.tagName === 'INPUT'
                          || target.tagName === 'TEXTAREA'
                          || target.contentEditable === 'true'
                          || target.closest('input, textarea, [contenteditable="true"]')

    if (!isInputElement) {
      e.preventDefault()
    }

    // Reset flag after a short delay to allow for toolbar interactions
    const timeoutId = setTimeout(() => {
      interactionRef.current = false
    }, timeout)

    // Return cleanup function
    return () => clearTimeout(timeoutId)
  }
}

/**
 * Creates a click-outside handler for editor blur detection
 * Supports both mouse and touch events for mobile compatibility
 */
export const createClickOutsideHandler = (
  editorContainerRef: React.RefObject<HTMLDivElement>,
  isToolbarInteractionRef: React.MutableRefObject<boolean>,
  onBlur: () => void,
) => {
  return (event: MouseEvent | TouchEvent) => {
    const target = event.target as HTMLElement
    if (!target) return

    // Check if click/touch is inside editor container
    if (editorContainerRef.current?.contains(target)) {
      return
    }

    // Check if click/touch is on toolbar element
    if (isToolbarElement(target)) {
      isToolbarInteractionRef.current = true
      return
    }

    // Click/touch is outside - trigger blur
    onBlur()
  }
}
